import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ChartBar, ArrowLeft, ShieldCheck, UserPlus, Key, DeviceMobile, FingerprintSimple } from "@phosphor-icons/react";
import { getStored, unlockBiometric, clearStored } from "@/lib/biometric";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [bioStored] = useState(() => getStored());
  const [bioUnlocking, setBioUnlocking] = useState(false);

  // If a biometric credential is enrolled on this device, surface a one-tap
  // "Unlock with FaceID / fingerprint" button at the top of the login form.
  const handleBiometricUnlock = async () => {
    setBioUnlocking(true);
    try {
      const { email, token } = await unlockBiometric();
      if (token) {
        // Push the cached JWT into the same cookie the regular login sets so
        // existing axios calls just work. The backend trusts either header
        // or cookie, so we set both via /auth/login-with-token if available
        // or fall back to a token cookie.
        document.cookie = `access_token=${token}; path=/; SameSite=Lax`;
        // Refresh AuthContext via /auth/me
        const me = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        // Use the same login flow but skip credential check. Simplest: do
        // a soft reload so AuthProvider picks up the freshly-set cookie.
        window.location.href = "/dashboard";
      } else {
        // No stored token — just prefill email and show the password field.
        document.getElementById("login-email-input")?.focus();
        setBioUnlocking(false);
        setError("Biometric verified — please enter your password to refresh the session.");
        if (email) {
          const ev = new Event("input", { bubbles: true });
          const el = document.querySelector('[data-testid="login-email-input"]');
          if (el) {
            el.value = email;
            el.dispatchEvent(ev);
          }
        }
      }
    } catch (e) {
      setBioUnlocking(false);
      setError(e.message || "Biometric unlock cancelled");
    }
  };
  const [view, setView] = useState("login");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Login
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Register: admin creds (kept for final submit)
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");

  // Register: user details
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regMobile, setRegMobile] = useState("");
  const [regCompany, setRegCompany] = useState("");

  // Reset (email OTP — Phase 28, replaces the Twilio SMS OTP)
  const [resetEmail, setResetEmail] = useState("");
  const [resetOtp, setResetOtp] = useState("");
  const [resetOtpSent, setResetOtpSent] = useState(false);
  const [resetNewPassword, setResetNewPassword] = useState("");
  const [resetDone, setResetDone] = useState(false);
  const [resetInfoMsg, setResetInfoMsg] = useState("");

  const fmt = (d) => {
    if (!d) return "Something went wrong.";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
    return String(d);
  };

  const handleLogin = async (e) => {
    e.preventDefault(); setError(""); setSubmitting(true);
    try { await login(loginEmail, loginPassword); }
    catch (err) { setError(fmt(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  // Step 1: Verify admin → go directly to details form
  const handleVerifyAdmin = async (e) => {
    e.preventDefault(); setError(""); setSubmitting(true);
    try {
      await axios.post(`${API}/auth/verify-admin`, { admin_email: adminEmail, admin_password: adminPassword });
      setView("register-details");
    } catch (err) { setError(fmt(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  // Step 2: Submit registration (admin creds + user details, no OTP)
  const handleRegister = async (e) => {
    e.preventDefault(); setError(""); setSubmitting(true);
    try {
      await axios.post(`${API}/auth/register`, {
        admin_email: adminEmail, admin_password: adminPassword,
        email: regEmail, password: regPassword, name: regName,
        mobile: regMobile, company_name: regCompany,
      });
      await login(regEmail, regPassword);
    } catch (err) { setError(fmt(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  // Password Reset via email OTP (Phase 28)
  const handleResetSendOtp = async (e) => {
    e.preventDefault(); setError(""); setResetInfoMsg(""); setSubmitting(true);
    try {
      const r = await axios.post(`${API}/auth/reset-password/send-email-otp`, { email: resetEmail });
      setResetOtpSent(true);
      setResetInfoMsg(r.data?.message || `Reset code sent to ${resetEmail}.`);
      // OTP is delivered ONLY via email now. The old `dev_otp` echo was
      // removed as a security fix — never surface reset codes in the UI.
    } catch (err) { setError(fmt(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const handleResetConfirm = async (e) => {
    e.preventDefault(); setError(""); setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password/confirm-email`, {
        email: resetEmail, otp: resetOtp, new_password: resetNewPassword,
      });
      setResetDone(true);
    } catch (err) { setError(fmt(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const resetAll = () => {
    setView("login"); setError(""); setAdminEmail(""); setAdminPassword("");
    setRegName(""); setRegEmail(""); setRegPassword(""); setRegMobile(""); setRegCompany("");
    setResetEmail(""); setResetOtp(""); setResetOtpSent(false); setResetNewPassword("");
    setResetDone(false); setResetInfoMsg("");
  };

  const BackButton = ({ to }) => (
    <button onClick={() => { setError(""); setView(to); }} className="flex items-center gap-1 text-slate-500 hover:text-slate-900 text-sm mb-4 transition-colors">
      <ArrowLeft size={16} /> Back
    </button>
  );

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 items-center justify-center p-12">
        <div>
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 bg-white rounded-sm flex items-center justify-center">
              <ChartBar size={40} weight="duotone" className="text-slate-900" />
            </div>
            <div>
              <h1 className="text-4xl font-black tracking-tighter text-white" style={{ fontFamily: "Chivo, sans-serif" }}>LokTech Dex Pro</h1>
              <p className="text-slate-400 text-sm uppercase tracking-wider">Hub</p>
            </div>
          </div>
          <p className="text-slate-300 text-lg max-w-md leading-relaxed">
            Manage invoices, track payments, and generate GST reports with ease. Built for Indian businesses.
          </p>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-slate-900 rounded-sm flex items-center justify-center">
              <ChartBar size={24} weight="duotone" className="text-white" />
            </div>
            <h1 className="text-xl font-black tracking-tight text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>LokTech Dex Pro</h1>
          </div>

          {error && <div data-testid="auth-error" className="mb-6 p-4 bg-red-50 border border-red-200 rounded-sm text-red-700 text-sm">{error}</div>}

          {/* ====== LOGIN ====== */}
          {view === "login" && (
            <>
              <h2 className="text-3xl font-black tracking-tighter text-slate-900 mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>Welcome back</h2>
              <p className="text-slate-500 text-sm mb-5">Sign in to your account</p>

              {/* Module chooser — Income Tax has its own separate, clean login page */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div data-testid="module-choice-gst" className="flex items-center justify-center gap-2 border-2 border-slate-900 bg-slate-900 text-white rounded-sm px-3 py-3 text-sm font-bold">
                  GST Software
                </div>
                <button
                  type="button"
                  data-testid="module-choice-incometax"
                  onClick={() => navigate("/income-tax/login")}
                  className="flex items-center justify-center gap-2 border-2 border-emerald-700 text-emerald-800 bg-emerald-50 hover:bg-emerald-100 rounded-sm px-3 py-3 text-sm font-bold transition-colors"
                >
                  Income Tax →
                </button>
              </div>

              {bioStored && (
                <div className="mb-6">
                  <button
                    type="button"
                    onClick={handleBiometricUnlock}
                    disabled={bioUnlocking}
                    data-testid="login-biometric-btn"
                    className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-sm border-2 border-emerald-500 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-50 text-emerald-800 font-bold transition"
                  >
                    <FingerprintSimple size={22} weight="fill" />
                    {bioUnlocking ? "Verifying…" : `Unlock with FaceID / Fingerprint`}
                  </button>
                  <p className="text-[11px] text-slate-400 text-center mt-2">
                    Signed in as <b>{bioStored.email}</b>{" "}
                    ·{" "}
                    <button
                      type="button"
                      onClick={() => { clearStored(); window.location.reload(); }}
                      className="underline hover:text-slate-700"
                    >
                      Use a different account
                    </button>
                  </p>
                  <div className="flex items-center gap-3 my-5">
                    <div className="flex-1 h-px bg-slate-200" />
                    <span className="text-[10px] font-bold text-slate-400 tracking-widest">OR</span>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>
                </div>
              )}

              <form onSubmit={handleLogin} className="space-y-5">
                <div><Label>Email</Label><Input type="email" data-testid="login-email-input" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} placeholder="you@example.com" required /></div>
                <div><Label>Password</Label><Input type="password" data-testid="login-password-input" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} placeholder="Enter password" required /></div>
                <Button type="submit" data-testid="login-submit-btn" disabled={submitting} className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3">
                  {submitting ? "Please wait..." : "Sign In"}
                </Button>
              </form>
              <div className="mt-6 flex justify-between text-sm">
                <button onClick={() => { setError(""); setView("register-admin"); }} data-testid="toggle-auth-mode" className="text-blue-600 font-semibold hover:underline">Register</button>
                <button onClick={() => { setError(""); setView("reset-step1"); }} data-testid="forgot-password-btn" className="text-slate-500 hover:text-slate-900 font-semibold">Forgot Password?</button>
              </div>
            </>
          )}

          {/* ====== REGISTER: Admin Verification ====== */}
          {view === "register-admin" && (
            <>
              <BackButton to="login" />
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-amber-100 rounded-sm flex items-center justify-center"><ShieldCheck size={24} weight="duotone" className="text-amber-700" /></div>
                <div>
                  <h2 className="text-2xl font-black tracking-tighter text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Admin Verification</h2>
                  <p className="text-slate-500 text-xs">Step 1 of 2 - Registration requires admin approval</p>
                </div>
              </div>
              <form onSubmit={handleVerifyAdmin} className="space-y-5">
                <div><Label>Admin Email</Label><Input type="email" data-testid="admin-email-input" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="Admin's email" required /></div>
                <div><Label>Admin Password</Label><Input type="password" data-testid="admin-password-input" value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} placeholder="Admin's password" required /></div>
                <Button type="submit" data-testid="verify-admin-btn" disabled={submitting} className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3">
                  {submitting ? "Verifying..." : "Verify Admin Access"}
                </Button>
              </form>
            </>
          )}

          {/* ====== REGISTER: User Details (admin verified, no OTP needed) ====== */}
          {view === "register-details" && (
            <>
              <BackButton to="register-admin" />
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-green-100 rounded-sm flex items-center justify-center"><UserPlus size={24} weight="duotone" className="text-green-700" /></div>
                <div>
                  <h2 className="text-2xl font-black tracking-tighter text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Create Account</h2>
                  <p className="text-slate-500 text-xs">Step 2 of 2 - Admin verified. Enter your details.</p>
                </div>
              </div>
              <form onSubmit={handleRegister} className="space-y-4">
                <div><Label>Full Name *</Label><Input data-testid="register-name-input" value={regName} onChange={(e) => setRegName(e.target.value)} required /></div>
                <div><Label>Email *</Label><Input type="email" data-testid="register-email-input" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} required /></div>
                <div><Label>Password *</Label><Input type="password" data-testid="register-password-input" value={regPassword} onChange={(e) => setRegPassword(e.target.value)} required /></div>
                <div><Label>Mobile</Label><Input data-testid="register-mobile-input" value={regMobile} onChange={(e) => setRegMobile(e.target.value)} placeholder="Optional" /></div>
                <div><Label>Company Name *</Label><Input data-testid="register-company-input" value={regCompany} onChange={(e) => setRegCompany(e.target.value)} required /></div>
                <Button type="submit" data-testid="register-submit-btn" disabled={submitting} className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3">
                  {submitting ? "Creating Account..." : "Create Account"}
                </Button>
              </form>
            </>
          )}

          {/* ====== PASSWORD RESET ====== */}
          {view === "reset-step1" && (
            <>
              <BackButton to="login" />
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-purple-100 rounded-sm flex items-center justify-center"><Key size={24} weight="duotone" className="text-purple-700" /></div>
                <div>
                  <h2 className="text-2xl font-black tracking-tighter text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Reset Password</h2>
                  <p className="text-slate-500 text-xs">A 6-digit code will be emailed to your registered address.</p>
                </div>
              </div>
              {!resetOtpSent ? (
                <form onSubmit={handleResetSendOtp} className="space-y-5">
                  <div>
                    <Label>Registered Email</Label>
                    <Input type="email" data-testid="reset-email-input" value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      placeholder="you@example.com" required autoComplete="email" />
                  </div>
                  <Button type="submit" data-testid="reset-send-otp-btn" disabled={submitting} className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3">
                    {submitting ? "Sending..." : "Email me a reset code"}
                  </Button>
                </form>
              ) : !resetDone ? (
                <form onSubmit={handleResetConfirm} className="space-y-5">
                  {resetInfoMsg && (
                    <p className="text-sm text-emerald-700 font-semibold" data-testid="reset-info-msg">{resetInfoMsg}</p>
                  )}
                  <div><Label>6-digit code</Label><Input data-testid="reset-otp-input" value={resetOtp} onChange={(e) => setResetOtp(e.target.value)} placeholder="123456" maxLength={6} required inputMode="numeric" /></div>
                  <div><Label>New Password</Label><Input type="password" data-testid="reset-new-password-input" value={resetNewPassword} onChange={(e) => setResetNewPassword(e.target.value)} placeholder="Min 6 characters" required /></div>
                  <Button type="submit" data-testid="reset-confirm-btn" disabled={submitting} className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3">
                    {submitting ? "Resetting..." : "Reset Password"}
                  </Button>
                </form>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"><ShieldCheck size={32} weight="duotone" className="text-green-600" /></div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Password Reset Successful</h3>
                  <p className="text-slate-500 mb-6">You can now login with your new password</p>
                  <Button onClick={resetAll} className="bg-slate-900 hover:bg-slate-800">Go to Login</Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
